import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Title } from 'react-admin';
import { AdminResponseDtoRoleEnum } from 'shared/api';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { getAdminData } from 'app/providers/auth-provider';
import { httpClient } from 'shared/services/http-client';
import { getErrorMessage } from 'shared/utils/get-error-message';

type AdminItem = {
  id: string;
  name: string;
  email: string;
  role: string;
};

type AdminsListResponse = {
  data?: AdminItem[];
  admins?: AdminItem[];
  total?: number;
  page?: number;
};

type AdminForm = {
  name: string;
  email: string;
  password: string;
  role: string;
};

const ROLE_OPTIONS = [
  { value: 'curator', label: 'Куратор' },
  { value: 'admin', label: 'Админ' },
  { value: 'askona', label: 'Askona' },
  { value: 'medsi', label: 'Medsi' },
];

const ROLE_LABELS: Record<string, string> = {
  curator: 'Куратор',
  admin: 'Админ',
  superadmin: 'Суперадмин',
  askona: 'Askona',
  medsi: 'Medsi',
};

const DEFAULT_FORM: AdminForm = {
  name: '',
  email: '',
  password: '',
  role: 'curator',
};

const PASSWORD_HINT =
  '8–32 символа: строчная, заглавная, цифра и спецсимвол (@$!%*?&)';

const isPasswordValid = (password: string) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,32}$/.test(
    password,
  );

const Admins = () => {
  const { userRole } = getAdminData();
  const isSuperadmin = userRole === AdminResponseDtoRoleEnum.Superadmin;

  const [items, setItems] = useState<AdminItem[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('curator');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState<AdminForm>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredHint = useMemo(
    () => ROLE_LABELS[roleFilter] || roleFilter || 'Все',
    [roleFilter],
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '1000');
      // Non-superadmin can only browse curators.
      const effectiveRole = isSuperadmin ? roleFilter : 'curator';
      if (effectiveRole) params.set('role', effectiveRole);

      const response = await httpClient(
        `${API_BASE_URL_ADMIN}${API_URL.admins}?${params.toString()}`,
      );
      const json = response.json as AdminsListResponse;
      setItems(json.data || json.admins || []);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка загрузки админов'));
    } finally {
      setIsLoading(false);
    }
  }, [isSuperadmin, roleFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setForm(DEFAULT_FORM);
    setFormError(null);
    setIsCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setFormError('Укажите имя');
      return;
    }
    if (!form.email.trim()) {
      setFormError('Укажите email');
      return;
    }
    if (!isPasswordValid(form.password)) {
      setFormError(PASSWORD_HINT);
      return;
    }

    setIsSaving(true);
    setFormError(null);
    try {
      await httpClient(`${API_BASE_URL_ADMIN}`, {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
        }),
        headers: new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      });
      setIsCreateOpen(false);
      await refresh();
    } catch (e: unknown) {
      setFormError(getErrorMessage(e, 'Не удалось создать администратора'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await httpClient(`${API_BASE_URL_ADMIN}${API_URL.adminById(deleteId)}`, {
        method: 'DELETE',
        headers: new Headers({ Accept: 'application/json' }),
      });
      setDeleteId(null);
      await refresh();
    } catch (e: unknown) {
      setDeleteError(getErrorMessage(e, 'Не удалось удалить администратора'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Title title="Админы" />
      <Card>
        <CardHeader
          title="Админы"
          subheader={
            isSuperadmin
              ? `Фильтр: ${filteredHint}. Создание доступно только суперадмину.`
              : 'Просмотр кураторов для назначения на курсы'
          }
          action={
            isSuperadmin ? (
              <Button variant="contained" onClick={openCreate}>
                Создать
              </Button>
            ) : null
          }
        />
        <CardContent>
          <Stack spacing={2}>
            {!isSuperadmin && (
              <Alert severity="info">
                Доступен только просмотр кураторов. Создание и удаление — у суперадмина.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {isLoading && <LinearProgress />}

            {isSuperadmin ? (
              <FormControl sx={{ maxWidth: 280 }}>
                <InputLabel>Роль</InputLabel>
                <Select
                  label="Роль"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(String(e.target.value))}
                >
                  <MenuItem value="">Все</MenuItem>
                  {ROLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="superadmin">Суперадмин</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Показаны только кураторы
              </Typography>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Имя</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Роль</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{ROLE_LABELS[item.role] || item.role}</TableCell>
                      <TableCell align="right">
                        {isSuperadmin && item.role !== 'superadmin' && (
                          <Button
                            color="error"
                            size="small"
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteId(item.id);
                            }}
                          >
                            Удалить
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!items.length && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary">
                          Админы не найдены
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={isCreateOpen}
        onClose={() => !isSaving && setIsCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Создать администратора</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Имя"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Пароль"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              helperText={PASSWORD_HINT}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Роль</InputLabel>
              <Select
                label="Роль"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: String(e.target.value) })}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateOpen(false)} disabled={isSaving}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={isSaving}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => !isDeleting && setDeleteId(null)}
      >
        <DialogTitle>Удалить администратора?</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
          <Typography>Действие необратимо.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={isDeleting}>
            Отмена
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admins;
